import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import DefectFormpopup from "./DefectForm";
import useSetLogDefect from "../../useQuery/useLogDefect";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  ListSubheader,
  TableRow,
  Button,
  Chip,
  styled,
  CircularProgress,
  Pagination,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  Drawer,
  Badge,
  Checkbox,
  Select,
} from "@mui/material";
import {
  useAddUserTagQuery,
  useGetCaseRunHistory,
  useSearchRunHistoryQuery,
  useGetTagsReportQuery,
  useGetUserTagsQuery,
  useGetUserTagsForSuiteQuery,
  useGetErrorCodesQuery,
  useGetAllDescriptionTags,
  useGetTCResultsListWithTagsForTSRQuery,
  useGetTestCaseResultsByUserTag,
} from "../../useQuery";

import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { v4 as uuidv4 } from "uuid";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LabelIcon from "@mui/icons-material/Label";
import { useNotification } from "../../hooks";
import { OpenInNew, ReportProblem, Visibility } from "@mui/icons-material";
import * as XLSX from "xlsx";
import TagModal from "./TagModalComponent";
import { DeleteIcon, DownloadIcon, PlusIcon } from "../../components-new/icons";
import { PageTitle } from "../../components-new/common";
import { openAuthedResource } from "../../utils/authedResource";

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#e0e0e0",
  fontSize: 14,
  fontWeight: 600,
  borderBottom: "none",
  background: "#2550D1",
  padding: "16px 16px",
}));

const StyledTableBodyCell = styled(TableCell)(({ theme }) => ({
  padding: "10px 16px",
  borderBottom: `1px solid rgba(226, 230, 243, 0.8)`,
  fontSize: 14,
  verticalAlign: "middle",
}));

const StatusBadge = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === "PASS" ? "#e8f5e9" : "#ffebee",
  color: status === "PASS" ? "#2e7d32" : "#d32f2f",
  fontWeight: 500,
  borderRadius: 41,
  height: 24,
  fontSize: "12px",
}));

const ErrorBox = styled(Box)(({ theme }) => ({
  padding: "8px",
  backgroundColor: "#ffebee",
  borderRadius: 4,
  borderLeft: "3px solid #d32f2f",
  margin: "4px 0",
  maxHeight: "80px",
  overflow: "auto",
  fontSize: "12px",
  maxWidth: "100px",
}));

const StyledMultiSelect = styled(Select)`
  border-radius: 5px;
  border: 1px solid #d1dbff;
  font-size: 16px;
  color: #2b3539;
  min-width: 200px;
  max-height: 300px;
  background-color: #ffffff;
  .MuiSelect-select {
    padding: 11px 12px;
  }
`;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: "300px",
    },
  },
};

const TestCaseDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const tsid = queryParams.get("tsId");
  const tsrid = queryParams.get("tsrid");
  const suiteName = queryParams.get("name");
  const runDate = queryParams.get("date");
  const prodId = queryParams.get("prodID");
  const notify = useNotification();
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    filterOne: "",
    filterTwo: "",
    filterThree: "",
  });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [textSearch, setTextSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [currentTestCaseResultId, setCurrentTestCaseResultId] = useState(null);
  const searchInputRef = useRef(null);
  const [selectedErrorCodes, setSelectedErrorCodes] = useState([]);
  const [selectedDescriptionTags, setSelectedDescriptionTags] = useState([]);
  const [isGeneratingTCResultsReport, setIsGeneratingTCResultsReport] = useState(false);
  const [isGeneratingTagsReport, setIsGeneratingTagsReport] = useState(false);
  const [defectOpen, setDefectOpen] = useState(false);
  const [defectObj, setDefectObj] = useState({});
  // const { mutate: logDefect, isPending } = useLogDefect();
  const { mutate: setlogDefect } = useSetLogDefect({
    onSuccess: () => {
      notify("Defect added successfully", "success");
      handleClose();
      setDefectObj({});
    },
    onError: (error) => {
      notify(`Error adding Defect: ${error.message}`, "error");
    },
  });

  useEffect(() => {
    const combinedQuery = [...selectedTags, textSearch].filter(Boolean).join(" ");
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(combinedQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedTags, textSearch]);

  useEffect(() => {
    if (document.activeElement === searchInputRef.current) {
      const cursorPosition = searchInputRef.current.selectionStart;
      setTimeout(() => {
        searchInputRef.current.focus();
        searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    }
  }, [debouncedSearchQuery]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: tcResultsWithTagsData,
    refetch: refetchTCResultsWithTags,
    isLoading: isLoadingTCResultsWithTags,
  } = useGetTCResultsListWithTagsForTSRQuery(tsrid, {
    enabled: false,
  });
  const { data: tagsData } = useGetUserTagsForSuiteQuery(tsrid, {
    enabled: !!tsrid,
  });

  const {
    data: testCaseResultsByUserTagData,
    refetch: refetchTestCaseResultsByUserTag,
    isLoading: isLoadingTestCaseResultsByUserTag,
  } = useGetTestCaseResultsByUserTag(selectedTags, tsrid, {
    enabled: false,
  });

  const {
    data: regularData,
    isLoading: isRegularLoading,
    isError: isRegularError,
    error: regularError,
    refetch: refetchRegular,
  } = useGetCaseRunHistory(tsid, tsrid, page, filters, {
    enabled: Boolean(!!tsid && (!debouncedSearchQuery || debouncedSearchQuery === "#")),
    staleTime: 0,
  });

  const { data: errorCodesResponse, isSuccess: isErrorCodesSuccess } = useGetErrorCodesQuery(
    tsrid,
    {
      enabled: Boolean(tsrid),
    },
  );

  const { data: descriptionTagsData, isSuccess: isDescriptionTagsSuccess } =
    useGetAllDescriptionTags(tsrid, {
      enabled: Boolean(tsrid),
    });

  const {
    data: searchData,
    isLoading: isSearchLoading,
    isError: isSearchError,
    error: searchError,
    refetch: refetchSearch,
  } = useSearchRunHistoryQuery(
    tsid,
    tsrid,
    debouncedSearchQuery.startsWith("#") ? debouncedSearchQuery : searchQuery,
    page,
    {
      issueTypes: selectedErrorCodes.length > 0 ? selectedErrorCodes : undefined,
      descriptionTags: selectedDescriptionTags.length > 0 ? selectedDescriptionTags : undefined,
    },
    {
      enabled: Boolean(
        !!tsid &&
          ((debouncedSearchQuery && debouncedSearchQuery.length > 1) ||
            selectedErrorCodes.length > 0 ||
            selectedDescriptionTags.length > 0),
      ),
      staleTime: 0,
    },
  );
  const {
    data: userTagsData,
    isLoading: isLoadingUserTags,
    refetch: refetchUserTags,
  } = useGetUserTagsQuery(currentTestCaseResultId, {
    enabled: false,
  });

  const hasSearchQuery = debouncedSearchQuery && debouncedSearchQuery.length > 1;
  const hasActiveFilters = selectedErrorCodes.length > 0 || selectedDescriptionTags.length > 0;
  const data = hasSearchQuery || hasActiveFilters ? searchData : regularData;
  const isLoading = hasSearchQuery || hasActiveFilters ? isSearchLoading : isRegularLoading;
  const isError = hasSearchQuery || hasActiveFilters ? isSearchError : isRegularError;
  const error = hasSearchQuery || hasActiveFilters ? searchError : regularError;

  const errorCodes = errorCodesResponse?.error_codes || errorCodesResponse;

  const descriptionTags = descriptionTagsData?.tcDescription_tag_list || [];

  const refetch = () => {
    if (debouncedSearchQuery && debouncedSearchQuery.length > 1) {
      refetchSearch();
    } else {
      refetchRegular();
    }
  };

  const { mutate: addTagMutation, isLoading: isAddingTag } = useAddUserTagQuery({
    onSuccess: () => {
      notify("Tag Added successfully", "success");
      setTagModalOpen(false);
      refetch();
    },
    onError: (error) => {
      notify(`Error Adding Tag: ${error.message || "Unknown error"}`, "error");
    },
  });

  const {
    data: tagsReportData,
    isLoading: isLoadingTagsReport,
    isError: isTagsReportError,
    refetch: refetchTagsReport,
  } = useGetTagsReportQuery(tsrid, {
    enabled: false,
  });

  const handleGenerateTCResultsReport = async () => {
    try {
      setIsGeneratingTCResultsReport(true);
      const result = await refetchTCResultsWithTags();

      if (result.data && result.data.success && result.data.testCaseResults) {
        generateTCResultsExcelReport(result.data);
      } else {
        notify("No test case results data available for export", "warning");
      }
    } catch (error) {
      console.error("Error generating TC results report:", error);
      notify("Error generating report: " + error.message, "error");
    } finally {
      setIsGeneratingTCResultsReport(false);
    }
  };

  const generateTCResultsExcelReport = (data) => {
    try {
      const headers = [
        "Test Case Name",
        "Test Case ID",
        "Comments",
        "Tags",
        "Executed Date",
        "Status",
        "Log File",
      ];
      const wsData = [headers];

      data.testCaseResults.forEach((result) => {
        wsData.push([
          result.test_case_name,
          result.test_case_id,
          result.user_comments || "",
          result.tags ? result.tags.join(", ") : "",
          result.Executed_Date,
          result.Status,
          result.logfile,
        ]);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      const colWidths = [
        { wch: 60 }, // Test Case Name
        { wch: 12 }, // Test Case ID
        { wch: 30 }, // Comments
        { wch: 30 }, // Tags
        { wch: 20 }, // Executed Date
        { wch: 20 }, // Status
        { wch: 100 }, // Log File
      ];
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Test Case Results");

      const dateStr = new Date().toISOString().replace(/[:.]/g, "-").substring(0, 19);
      const fileName = `TestCaseResults_${tsrid}_${dateStr}.xlsx`;
      XLSX.writeFile(wb, fileName);

      notify("Test case results report generated successfully", "success");
    } catch (error) {
      console.error("Excel generation error:", error);
      notify("Error in Excel generation: " + error.message, "error");
    }
  };
  // Generate Excel Report
  const generateExcelReport = (data) => {
    if (!data || !data.userNotes || data.userNotes.length === 0) {
      notify("No tags data to generate report", "warning");
      return;
    }

    try {
      const wsData = [["Tag", "Count", "Comments"]];

      data.userNotes.forEach((tag) => {
        wsData.push([tag.note, tag.count, tag.topComments.join(", ")]);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      const colWidths = [{ wch: 20 }, { wch: 10 }, { wch: 50 }];
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Tags Report");

      XLSX.writeFile(wb, `Report-${tsrid}.xlsx`);

      notify("Tags report generated successfully", "success");
    } catch (error) {
      console.error("Excel generation error:", error);
      notify("Error in Excel generation: " + error.message, "error");
    }
  };

  const generateTagsExcelReport = (data) => {
    try {
      if (!data || !data.success) {
        notify("No valid data to generate report", "warning");
        return;
      }

      const results = data.results;
      const tagNames = Object.keys(results);

      if (tagNames.length === 0) {
        notify("No tags found in the data", "warning");
        return;
      }

      const wb = XLSX.utils.book_new();
      const summaryHeaders = ["Tag", "Test Case Count"];
      const summaryData = [summaryHeaders];

      tagNames.forEach((tagName) => {
        summaryData.push([tagName, results[tagName].count, results[tagName].description || ""]);
      });

      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      const summaryColWidths = [{ wch: 40 }, { wch: 15 }];
      summaryWs["!cols"] = summaryColWidths;

      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
      tagNames.forEach((tagName) => {
        const tagData = results[tagName];
        if (!tagData.testCases || tagData.testCases.length === 0) return;

        const headers = ["Test Case Name", "Test Case ID", "Comments", "Tag"];
        const wsData = [headers];

        tagData.testCases.forEach((testCase) => {
          wsData.push([
            testCase.test_case_name,
            testCase.test_case_id,
            testCase.user_comments || "",
            tagName,
          ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        const colWidths = [{ wch: 70 }, { wch: 15 }, { wch: 60 }, { wch: 30 }];
        ws["!cols"] = colWidths;
        const sheetName = tagName.replace(/[#*?:/\\[\]]/g, "").substring(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName || "Tag");
      });

      const dateStr = new Date().toISOString().replace(/[:.]/g, "-").substring(0, 19);
      const fileName = `TagsReport_${tsrid}_${dateStr}.xlsx`;

      XLSX.writeFile(wb, fileName);
      notify("Tags report generated successfully", "success");
    } catch (error) {
      console.error("Excel generation error:", error);
      notify(`Error generating Excel report: ${error.message || "Unknown error"}`, "error");
    }
  };

  const handleGetTagsReport = async () => {
    try {
      if (selectedTags.length === 0) {
        notify("Please select at least one tag", "warning");
        return;
      }

      setIsGeneratingTagsReport(true);

      const result = await refetchTestCaseResultsByUserTag();
      console.log("Tags report result:", result.data);

      if (result.data && result.data.success) {
        generateTagsExcelReport(result.data);
      } else {
        notify("No valid data received from the server", "warning");
      }
    } catch (error) {
      console.error("Error fetching tags report:", error);
      notify("Error fetching tags report: " + error.message, "error");
    } finally {
      setIsGeneratingTagsReport(false);
    }
  };
  const handleViewResults = (currentTestCase) => {
    window.open(
      `/new/reports?tcrid=${currentTestCase.idtest_case_results}&name=${encodeURIComponent(suiteName)}&date=${runDate}&tcname=${encodeURIComponent(currentTestCase.testcase)}&tcRundate=${currentTestCase.date}`,
      "_blank",
    );
  };

  const handleGenerateTagsReport = async () => {
    try {
      const result = await refetchTagsReport();
      if (result.data) {
        generateExcelReport(result.data);
      }
    } catch (error) {
      notify("Error generating tags report: " + error.message, "error");
    }
  };

  useEffect(() => {
    if (data?.testcaseresult?.length > 0 && !selectedTestCase) {
      setSelectedTestCase(data.testcaseresult[0].latest.Test_Case_Id);
    }
  }, [data, selectedTestCase]);

  useEffect(() => {
    if (data?.testcaseresult?.length > 0) {
      setSelectedTestCase(data.testcaseresult[0].latest.Test_Case_Id);
      testCaseForLog(data.testcaseresult[0].latest);
    }
  }, [page, data]);

  function testCaseForLog(testcase) {
    setDefectObj((prev) => ({
      ...prev,
      tcid: testcase.Test_Case_Id,
      tcrid: testcase.idtest_case_results,
      tcname: testcase.testcase,
      status: testcase.Status,
    }));
  }
  const handleTestCaseSelect = (testcase) => {
    console.log(testcase, "selected");
    testCaseForLog(testcase);
    setSelectedTestCase(testcase.Test_Case_Id);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleViewFailureAnalysis = (testCaseResultId, testCaseName) => {
    window.open(
      `/new/defect-analysis?tsrid=${testCaseResultId}&&tcname=${encodeURIComponent(testCaseName)}`,
      "_blank",
    );
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    refetch();
    setFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      filterOne: "",
      filterTwo: "",
      filterThree: "",
    });
    setSelectedErrorCodes([]);
    setSelectedDescriptionTags([]);
    setPage(1);
    refetch();
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value === "") {
      setPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  useEffect(() => {
    if (tagModalOpen && currentTestCaseResultId) {
      refetchUserTags();
    }
  }, [currentTestCaseResultId, tagModalOpen, refetchUserTags]);

  const handleOpenTagModal = (testCaseResultId) => {
    setCurrentTestCaseResultId(testCaseResultId);
    setTagModalOpen(true);
  };
  const handleCloseTagModal = () => {
    setTagModalOpen(false);
  };

  const handleAddTag = (testCaseResultId, tagData) => {
    addTagMutation({
      testCaseResultId,
      tag: tagData.tag,
      comment: tagData.comment,
    });
  };

  const displayTestCases = data?.testcaseresult || [];

  const currentTestCase = data?.testcaseresult?.find(
    (tc) => tc.latest.Test_Case_Id === selectedTestCase,
  );

  const handleViewLog = (logfile, testcase) => {
    if (!logfile) return;

    openAuthedResource(logfile, { filename: `${testcase || "test-case"}.log` }).catch((error) =>
      notify(error.message || "Failed to open the log file.", "error"),
    );
  };
const handleOpenInStudio = (testCaseId, testCaseName) => {
  navigate("/new/test-studio", {
    state: {
      fromExecutionReports: true,
      idtest_case: testCaseId,
      Test_Case: testCaseName,
    },
  });
};
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">Error loading test case data: {error.message}</Typography>
      </Box>
    );
  }

  const formatDuration = (seconds) => {
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };

  const mapRunData = (runData) => {
    if (!runData) return null;
    return {
      runId: runData.idtest_case_results,
      date: runData.Executed_Date,
      status: runData.Status,
      duration: formatDuration(runData.Duration),
      browser: runData.Browser,
      error: runData.issues || "",
      logfile: runData.logfile,
      executedBy: runData.Executed_By,
      testcase: runData.testcase,
      idtest_case_results: runData.idtest_case_results,
      Status: runData.Status,
      issuetype: runData.issuetype,
      user_note: runData.user_note,
    };
  };

  const testRuns = currentTestCase
    ? [
        { data: mapRunData(currentTestCase.latest), label: "Latest Run" },
        ...(Array.isArray(currentTestCase.run_minus_one)
          ? currentTestCase.run_minus_one.map((run) => ({
              data: mapRunData(run),
              label: "Run-1",
            }))
          : currentTestCase.run_minus_one
            ? [{ data: mapRunData(currentTestCase.run_minus_one), label: "Run-1" }]
            : []),

        ...(Array.isArray(currentTestCase.run_minus_two)
          ? currentTestCase.run_minus_two.map((run) => ({
              data: mapRunData(run),
              label: "Run-2",
            }))
          : currentTestCase.run_minus_two
            ? [{ data: mapRunData(currentTestCase.run_minus_two), label: "Run-2" }]
            : []),
      ].filter((run) => run.data !== null)
    : [];

  const filterOptions = [
    { value: "", label: "Any" },
    { value: "PASS", label: "Pass" },
    { value: "FAIL", label: "Fail" },
  ];

  const activeFilterCount = Object.values(filters).filter((val) => val !== "").length;

  const handleClose = () => {
    setDefectOpen(false);
  };

  const defectSubmit = (formData) => {
    if (!defectObj.tcid) {
      notify("Please select a test case to log a defect", "warning");
      handleClose();
      return;
    }
    setlogDefect({
      ...defectObj,
      projectKey: formData.projectkey,
      message: formData.message,
      issueType: formData.issueType,
      productId: prodId,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <DefectFormpopup
        defectOpen={defectOpen}
        defectClose={handleClose}
        defectSubmit={defectSubmit}
      />
      <Box sx={{ borderBottom: "2px solid rgba(226, 230, 243, 0.8)", bgcolor: "#f8fafc" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <PageTitle title={"Recent Runs History"} variant="h5" />
            <Chip
              label={`${data?.total_count || 0} tests`}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 600,
                color: "black",
                padding: "4px 8px",
                borderColor: "grey.400",
                borderRadius: "4px",
                "& .MuiChip-label": {
                  color: "black",
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{
                textTransform: "none",
                height: "45px",
                bgcolor: "#2550D1",
              }}
              startIcon={<ReportProblem />}
              onClick={() => {
                setDefectOpen(true);
              }}
            >
              Log Defect
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StyledMultiSelect
                multiple
                value={[...selectedTags, ...selectedDescriptionTags]}
                onChange={(e) => {
                  const selectedValues = e.target.value;
                  const regularTags = new Set();
                  const descriptionTags = new Set();
                  selectedValues.forEach((value) => {
                    if (value.startsWith("desc:")) {
                      descriptionTags.add(value.substring(5));
                    } else {
                      const withoutPrefix = selectedDescriptionTags.includes(value);
                      if (withoutPrefix) {
                        descriptionTags.add(value);
                      } else {
                        regularTags.add(value);
                      }
                    }
                  });

                  setSelectedTags([...regularTags]);
                  setSelectedDescriptionTags([...descriptionTags]);
                  setPage(1);
                }}
                displayEmpty
                MenuProps={MenuProps}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "Filter by tags";
                  }
                  return (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selected.map((tag) => {
                        const isDescTag = tag.startsWith("desc:");
                        const displayTag = isDescTag ? tag.substring(5) : tag;
                        return (
                          <Chip
                            key={tag}
                            label={displayTag}
                            size="small"
                            color={isDescTag ? "secondary" : "primary"}
                            onDelete={() => {
                              if (isDescTag) {
                                setSelectedDescriptionTags(
                                  selectedDescriptionTags.filter((t) => t !== displayTag),
                                );
                              } else {
                                setSelectedTags(selectedTags.filter((t) => t !== tag));
                              }
                              setPage(1);
                            }}
                          />
                        );
                      })}
                    </Box>
                  );
                }}
              >
                {/* Regular Tags Group */}
                <ListSubheader>Regular Tags</ListSubheader>
                {tagsData?.tag_list?.map((tag) => (
                  <MenuItem key={tag.tag} value={tag.tag}>
                    <Checkbox checked={selectedTags.indexOf(tag.tag) > -1} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body2">{tag.tag}</Typography>
                      <Chip label={tag.count} size="small" sx={{ ml: 1 }} />
                    </Box>
                  </MenuItem>
                ))}

                {/* Description Tags Group */}
                <ListSubheader>Description Tags</ListSubheader>
                {descriptionTags?.map((tag) => {
                  const value = `desc:${tag.tag}`;
                  return (
                    <MenuItem key={value} value={value}>
                      <Checkbox checked={selectedDescriptionTags.indexOf(tag.tag) > -1} />
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <Typography variant="body2">{tag.tag}</Typography>
                        <Chip label={tag.count} size="small" sx={{ ml: 1 }} color="secondary" />
                      </Box>
                    </MenuItem>
                  );
                })}
              </StyledMultiSelect>

              {(selectedTags.length > 0 || selectedDescriptionTags.length > 0) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedTags([]);
                    setSelectedDescriptionTags([]);
                    setPage(1);
                  }}
                  startIcon={<DeleteIcon />}
                  sx={{
                    color: "#828286",
                    background: "#ffffff",
                    height: "44px",
                    border: "1px solid #E2E6F3",
                    outline: 0,
                    padding: "0 12px",
                    textTransform: "capitalize",
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>

            {/* Error code filter */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StyledMultiSelect
                multiple
                value={selectedErrorCodes}
                onChange={(e) => setSelectedErrorCodes(e.target.value)}
                displayEmpty
                MenuProps={MenuProps}
                placeholder="Select error codes"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "Select error codes";
                  }
                  return (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selected.map((code) => (
                        <Chip
                          key={code}
                          label={code}
                          size="small"
                          onDelete={() => {
                            setSelectedErrorCodes(selected.filter((c) => c !== code));
                            setPage(1);
                          }}
                        />
                      ))}
                    </Box>
                  );
                }}
              >
                {errorCodes &&
                  Object.entries(errorCodes).map(([code, details]) => (
                    <MenuItem key={code} value={code}>
                      <Checkbox checked={selectedErrorCodes.indexOf(code) > -1} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>
                          {code} - {details.message}
                        </span>
                        <Chip label={details.failedTestCasesCount} size="small" sx={{ ml: 1 }} />
                      </Box>
                    </MenuItem>
                  ))}
              </StyledMultiSelect>

              {selectedErrorCodes.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedErrorCodes([]);
                    setPage(1);
                  }}
                  startIcon={<DeleteIcon />}
                  sx={{
                    color: "#828286",
                    background: "#ffffff",
                    height: "44px",
                    border: "1px solid #E2E6F3",
                    outline: 0,
                    padding: "0 12px",
                    textTransform: "capitalize",
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>

            {/* Search input */}
            <TextField
              inputRef={searchInputRef}
              size="small"
              placeholder="Search test cases..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                width: 240,
                "& .MuiInputBase-root": {
                  height: 44,
                },
                bgcolor: "#ffffff",
                border: "1px solid #E2E6F3",
                outline: 0,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Filter button */}
            <Tooltip title="Filter results">
              <Badge badgeContent={activeFilterCount} color="primary" variant="dot">
                <Button
                  startIcon={<FilterAltIcon />}
                  variant={activeFilterCount > 0 ? "contained" : "outlined"}
                  sx={{
                    color: "#828286",
                    background: "#ffffff",
                    height: "44px",
                    border: "1px solid #E2E6F3",
                    outline: 0,
                    textTransform: "capitalize",
                  }}
                  size="medium"
                  onClick={() => setFilterDrawerOpen(true)}
                >
                  Filters
                </Button>
              </Badge>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Main content area */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Left sidebar - Test cases list */}
        <Paper
          elevation={0}
          sx={{
            width: 280,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRight: "2px solid rgba(226, 230, 243, 0.8)",
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.5,
              borderBottom: "2px solid rgba(226, 230, 243, 0.8)",
              backgroundColor: "#f8fafc",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#232d3d">
              Test Cases
              {debouncedSearchQuery && (
                <Chip
                  size="small"
                  label={`Search: ${debouncedSearchQuery}`}
                  color="primary"
                  sx={{ ml: 1, fontSize: "10px" }}
                  onDelete={() => {
                    setSelectedTags([]);
                    setTextSearch("");
                  }}
                />
              )}
            </Typography>
            {data?.total_pages > 1 && (
              <Pagination
                count={data?.total_pages || 1}
                page={page}
                onChange={handlePageChange}
                size="small"
                siblingCount={0}
              />
            )}
          </Box>

          <List disablePadding sx={{ flex: 1, overflow: "auto" }}>
            {displayTestCases.length > 0 ? (
              displayTestCases.map((testCase) => (
                <React.Fragment key={uuidv4()}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedTestCase === testCase.latest.Test_Case_Id}
                      onClick={() => handleTestCaseSelect(testCase.latest)}
                      sx={{
                        px: 1.5,
                        py: 1.5,
                        "&.Mui-selected": {
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                          borderLeft: "3px solid",
                          borderColor: "primary.main",
                          pl: 1,
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                            {testCase.latest.testcase.split("/")[1] || testCase.latest.testcase}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(testCase.latest.Executed_Date).toLocaleString()}
                            </Typography>
                            <StatusBadge
                              size="small"
                              status={testCase.latest.Status}
                              label={testCase.latest.Status}
                            />
                          </Box>
                        }
                      />
                          <Tooltip title="Open in Studio">
                          <IconButton
                            size="small"
                           onClick={(e) => {
                                e.stopPropagation();
                                handleOpenInStudio(testCase.latest.Test_Case_Id, testCase.latest.testcase);
                              }}
                            sx={{ color: "#2550D1", flexShrink: 0 }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                    </ListItemButton>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary" variant="body2">
                  {debouncedSearchQuery ? "No matching test cases found" : "No test cases found"}
                </Typography>
              </Box>
            )}
          </List>
        </Paper>

        {/* Right content area */}
        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {currentTestCase ? (
            <>
              {/* Test Case Header */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: 0,
                  borderBottom: "2px solid rgba(226, 230, 243, 0.8)",
                  bgcolor: "#ffffff",
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={600} maxWidth={750}>
                        {currentTestCase.latest.testcase}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 2 }}>
                        <Chip
                          icon={<ErrorOutlineIcon fontSize="small" />}
                          label={`Error: ${currentTestCase.latest.issuetype}`}
                          color="error"
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          icon={<HistoryIcon fontSize="small" />}
                          label={`${testRuns.length} runs`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        ml: 2,
                      }}
                    >
                      <Button
                        onClick={handleGenerateTCResultsReport}
                        sx={{
                          bgcolor: "#4CAF50",
                          color: "#ffffff",
                          padding: "8px 16px",
                          minWidth: "180px",
                          height: "48px",
                          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          "&:hover": {
                            bgcolor: "#45a049",
                            boxShadow: "none",
                          },
                        }}
                        endIcon={<DownloadIcon />}
                        disabled={isLoadingTCResultsWithTags || isGeneratingTCResultsReport}
                      >
                        {isLoadingTCResultsWithTags || isGeneratingTCResultsReport
                          ? "Generating..."
                          : "Export Test Results"}
                      </Button>
                      <Button
                        onClick={handleGenerateTagsReport}
                        sx={{
                          bgcolor: "#FF5336",
                          color: "#ffffff",
                          padding: "8px 16px",
                          minWidth: "180px",
                          height: "48px",
                          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          "&:hover": {
                            bgcolor: "#FF5336",
                            boxShadow: "none",
                          },
                        }}
                        endIcon={<DownloadIcon />}
                        disabled={isLoadingTagsReport}
                      >
                        {isLoadingTagsReport ? "Generating..." : "Summary Report"}
                      </Button>

                      <Button
                        onClick={handleGetTagsReport}
                        startIcon={<LabelIcon />}
                        sx={{
                          bgcolor: "#1976D2",

                          padding: "8px 16px",
                          minWidth: "180px",
                          height: "48px",
                          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
                          color: "#ffffff",
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          "&:hover": {
                            bgcolor: "#1976D2",
                            boxShadow: "none",
                          },
                        }}
                        disabled={isLoadingTestCaseResultsByUserTag || selectedTags.length === 0}
                      >
                        {isLoadingTestCaseResultsByUserTag ? "Loading..." : "Tags Report"}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Run History Table */}
              <TableContainer sx={{ flex: 1, overflow: "auto" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Run</StyledTableCell>
                      <StyledTableCell>Date</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Duration</StyledTableCell>
                      <StyledTableCell>Browser</StyledTableCell>
                      <StyledTableCell>Error</StyledTableCell>
                      <StyledTableCell>Tags</StyledTableCell>
                      <StyledTableCell>Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testRuns.map((run, index) => (
                      <TableRow key={`run-${index}`} hover>
                        <StyledTableBodyCell>
                          <Chip
                            label={run.label}
                            size="small"
                            color={index === 0 ? "primary" : "default"}
                            variant={index === 0 ? "filled" : "outlined"}
                            sx={{ fontSize: "11px", height: "24px" }}
                          />
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          {new Date(run.data.date).toLocaleString()}
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <StatusBadge status={run.data.status} label={run.data.status} />
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>{run.data.duration}</StyledTableBodyCell>
                        <StyledTableBodyCell>{run.data.browser}</StyledTableBodyCell>
                        <StyledTableBodyCell>
                          {run.data.status === "FAIL" && run.data.error ? (
                            <ErrorBox>{run.data.error}</ErrorBox>
                          ) : (
                            "-"
                          )}
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>{run.data.user_note}</StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                sx={{
                                  bgcolor: "#FF5336",
                                  color: "#ffffff",
                                  padding: "8px 16px",
                                  height: "36px",
                                  boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
                                  "&:hover": {
                                    bgcolor: "#FF5336",
                                    boxShadow: "none",
                                  },
                                }}
                                onClick={() => handleViewResults(run.data)}
                                endIcon={<OpenInNew />}
                              >
                                Result
                              </Button>
                              <Button
                                size="small"
                                endIcon={<ArrowOutwardIcon fontSize="small" />}
                                onClick={() => handleViewLog(run.data.logfile, run.data.testcase)}
                                disabled={!run.data.logfile}
                                sx={{
                                  bgcolor: "#FF5336",
                                  color: "#ffffff",
                                  padding: "8px 16px",
                                  height: "36px",
                                  boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
                                  "&:hover": {
                                    bgcolor: "#FF5336",
                                    boxShadow: "none",
                                  },
                                }}
                              >
                                Log
                              </Button>
                              {run.data.Status === "FAIL" && (
                                <Button
                                  size="small"
                                  endIcon={<ArrowOutwardIcon fontSize="small" />}
                                  onClick={() =>
                                    handleViewFailureAnalysis(
                                      run.data.idtest_case_results,
                                      run.data.testcase,
                                    )
                                  }
                                  sx={{
                                    bgcolor: "#FF5336",
                                    color: "#ffffff",
                                    padding: "8px 16px",
                                    height: "36px",
                                    boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
                                    "&:hover": {
                                      bgcolor: "#FF5336",
                                      boxShadow: "none",
                                    },
                                  }}
                                >
                                  Analysis
                                </Button>
                              )}
                              <Button
                                size="small"
                                startIcon={<PlusIcon fontSize="small" />}
                                onClick={() => handleOpenTagModal(run.data.idtest_case_results)}
                                sx={{
                                  bgcolor: "#FF5336",
                                  color: "#ffffff",
                                  padding: "8px 16px",
                                  height: "36px",
                                  boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
                                  "&:hover": {
                                    bgcolor: "#FF5336",
                                    boxShadow: "none",
                                  },
                                }}
                              >
                                Tags
                              </Button>
                            </Box>
                          </Box>
                        </StyledTableBodyCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography color="text.secondary">Select a test case to view details</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: { width: 320, p: 3, bgcolor: "#ffffff" },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h6" fontWeight={600} color="#232d3d">
            Filter Test Runs
          </Typography>
          <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Status Filters
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Latest Run"
            name="filterOne"
            value={filters.filterOne}
            onChange={handleFilterChange}
            sx={{ mb: 2 }}
          >
            {filterOptions.map((option) => (
              <MenuItem key={`filterOne-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Run-1"
            name="filterTwo"
            value={filters.filterTwo}
            onChange={handleFilterChange}
            sx={{ mb: 2 }}
          >
            {filterOptions.map((option) => (
              <MenuItem key={`filterTwo-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Run-2"
            name="filterThree"
            value={filters.filterThree}
            onChange={handleFilterChange}
            sx={{ mb: 3 }}
          >
            {filterOptions.map((option) => (
              <MenuItem key={`filterThree-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button variant="contained" fullWidth onClick={handleApplyFilters}>
              Apply Filters
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Drawer>

      {/* Tag Modal Component */}
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        onSubmit={handleAddTag}
        isSubmitting={isAddingTag}
        testCaseResultId={currentTestCaseResultId}
        existingTags={userTagsData?.user_note || ""}
        existingComment={userTagsData?.user_comments || ""}
        isLoading={isLoadingUserTags}
      />
    </Box>
  );
};

export default TestCaseDetailsPage;
